import { Info } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const FONT_WEIGHT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: '300', label: '300 — Light' },
  { value: '400', label: '400 — Regular' },
  { value: '500', label: '500 — Medium' },
  { value: '600', label: '600 — Semi Bold' },
  { value: '700', label: '700 — Bold' },
  { value: '800', label: '800 — Extra Bold' },
];

export const InfoTip = ({ text }: { text: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className='ml-1 inline size-3.5 cursor-help text-muted-foreground' />
      </TooltipTrigger>
      <TooltipContent side='top' className='max-w-xs text-xs'>
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface ColorFieldProps {
  id: string;
  label: React.ReactNode;
  /** Raw config value. Empty string when the user hasn't set one. */
  value: string;
  /** Used as the color-input fallback and the text-input placeholder. */
  defaultValue: string;
  /** When true, the text input shows the default when unset (original behavior for core colors). */
  showDefaultInTextInput?: boolean;
  onChange: (value: string) => void;
}

export const ColorField = ({
  id,
  label,
  value,
  defaultValue,
  showDefaultInTextInput = false,
  onChange,
}: ColorFieldProps) => (
  <div className='space-y-2'>
    <Label htmlFor={id}>{label}</Label>
    <div className='flex items-center gap-2'>
      <input
        type='color'
        id={id}
        value={value || defaultValue}
        onChange={(e) => onChange(e.target.value)}
        className='h-9 w-12 cursor-pointer rounded border p-0.5'
      />
      <Input
        value={showDefaultInTextInput ? value || defaultValue : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={defaultValue}
        className='flex-1'
      />
    </div>
  </div>
);

interface FontFieldProps {
  id: string;
  label: React.ReactNode;
  fontValue: string;
  weightValue: string;
  placeholder: string;
  error?: string;
  onFontChange: (value: string) => void;
  onWeightChange: (value: string) => void;
  onBlur: () => void;
}

export const FontField = ({
  id,
  label,
  fontValue,
  weightValue,
  placeholder,
  error,
  onFontChange,
  onWeightChange,
  onBlur,
}: FontFieldProps) => (
  <div className='space-y-2'>
    <Label htmlFor={id}>{label}</Label>
    <div className='flex gap-2'>
      <Input
        id={id}
        value={fontValue}
        onChange={(e) => onFontChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className='flex-1'
      />
      <Select value={weightValue} onValueChange={onWeightChange}>
        <SelectTrigger className='w-[140px]'>
          <SelectValue placeholder='Weight' />
        </SelectTrigger>
        <SelectContent>
          {FONT_WEIGHT_OPTIONS.map((opt) => (
            <SelectItem
              key={opt.value || '_default'}
              value={opt.value || '_default'}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    {error && <p className='text-xs text-destructive'>{error}</p>}
  </div>
);
